import type { OrderSheetRow } from '../types/nursery';

/**
 * The order sheet's arrangement: rows grouped by tree type (pot size, bare
 * root grade), then by crop, so the page reads like the printed order form
 * staff already know — and the filters that narrow it.
 */

export type SheetShow = 'all' | 'free' | 'ordered';

export interface SheetFilters {
  show: SheetShow;
  search: string;
  /** A tree type id, or null for every size. */
  treeTypeId: number | null;
  /** A crop as the sheet heads it ("Lemon", or "Other" for none); null or absent for every crop. */
  crop?: string | null;
}

export interface SheetCrop {
  crop: string;
  rows: OrderSheetRow[];
}

export interface SheetSection {
  treeTypeId: number;
  treeTypeName: string;
  treeTypeCode: string;
  crops: SheetCrop[];
}

/** Rows with no crop on their cultivar. Sorted after every named crop. */
export const NO_CROP = 'Other';

/** The crop a row is listed under. */
const cropOf = (row: OrderSheetRow) => row.crop_type_name?.trim() || NO_CROP;

/**
 * What a typed quantity means: blank is 0 (remove the line), digits are that
 * many, anything else is null — not a quantity.
 */
export function parseQuantity(text: string): number | null {
  const t = text.trim();
  if (t === '') return 0;
  if (!/^\d{1,7}$/.test(t)) return null;
  return parseInt(t, 10);
}

/** What a line is, for the server: a stock item or an umbrella product. */
export function rowSubject(row: OrderSheetRow): { stock_item_id: number } | { product_id: number } {
  return row.product_id != null ? { product_id: row.product_id } : { stock_item_id: row.stock_item_id as number };
}

/**
 * Narrow the rows. `ordered` is the quantity each row holds right now (typed
 * or saved), by key. A row with a quantity survives the stock filter — hiding
 * something already on the order would read as though it had been removed.
 */
export function filterSheet(rows: OrderSheetRow[], filters: SheetFilters, ordered: Record<string, number>): OrderSheetRow[] {
  const words = filters.search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  return rows.filter((row) => {
    const qty = ordered[row.key] ?? 0;
    if (filters.treeTypeId != null && row.tree_type_id !== filters.treeTypeId) return false;
    if (filters.crop != null && cropOf(row) !== filters.crop) return false;
    if (filters.show === 'ordered' && qty <= 0) return false;
    if (filters.show === 'free' && row.free <= 0 && qty <= 0) return false;
    if (words.length) {
      const text = [row.name, row.rootstock_name, row.crop_type_name, row.sku_code, row.tree_type_code, row.tree_type_name]
        .filter(Boolean).join(' ').toLowerCase();
      if (!words.every((w) => text.includes(w))) return false;
    }
    return true;
  });
}

const byText = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true });

/**
 * Tree types in their catalogue order, crops alphabetically ("Other" last),
 * and within a crop the umbrella products first, then stock items by name and
 * rootstock.
 */
export function groupSheet(rows: OrderSheetRow[]): SheetSection[] {
  const sections = new Map<number, { section: SheetSection; sort: number; crops: Map<string, OrderSheetRow[]> }>();
  for (const row of rows) {
    let entry = sections.get(row.tree_type_id);
    if (!entry) {
      entry = {
        section: { treeTypeId: row.tree_type_id, treeTypeName: row.tree_type_name, treeTypeCode: row.tree_type_code, crops: [] },
        sort: row.tree_type_sort,
        crops: new Map(),
      };
      sections.set(row.tree_type_id, entry);
    }
    const crop = cropOf(row);
    const list = entry.crops.get(crop);
    if (list) list.push(row);
    else entry.crops.set(crop, [row]);
  }

  return [...sections.values()]
    .sort((a, b) => a.sort - b.sort || byText(a.section.treeTypeName, b.section.treeTypeName))
    .map(({ section, crops }) => ({
      ...section,
      crops: [...crops.entries()]
        .sort(([a], [b]) => Number(a === NO_CROP) - Number(b === NO_CROP) || byText(a, b))
        .map(([crop, cropRows]) => ({
          crop,
          rows: [...cropRows].sort((a, b) =>
            Number(a.product_id == null) - Number(b.product_id == null)
            || byText(a.name, b.name)
            || byText(a.rootstock_name ?? '', b.rootstock_name ?? '')),
        })),
    }));
}

/** The tree types on the sheet, in catalogue order, for the size filter. */
export function sheetTreeTypes(rows: OrderSheetRow[]): { id: number; name: string }[] {
  return groupSheet(rows).map((s) => ({ id: s.treeTypeId, name: s.treeTypeName }));
}

/** The crops on the sheet, alphabetically with "Other" last, for the crop filter. */
export function sheetCrops(rows: OrderSheetRow[]): string[] {
  return [...new Set(rows.map(cropOf))]
    .sort((a, b) => Number(a === NO_CROP) - Number(b === NO_CROP) || byText(a, b));
}

/** Lines and trees entered, from the quantities by key. */
export function sheetCounts(ordered: Record<string, number>): { lines: number; trees: number } {
  let lines = 0;
  let trees = 0;
  for (const qty of Object.values(ordered)) {
    if (qty > 0) { lines += 1; trees += qty; }
  }
  return { lines, trees };
}
